import { useTranslation } from 'react-i18next';
import { X, KeyRound, UserPlus } from 'lucide-react';
import { useOverlayBackButton } from '../../hooks/useOverlayBackButton';
import { useModaleAccessibile } from '../../hooks/useModaleAccessibile';
// `.modal.dashboard-modal.play-online-modal` e la sua testata vivono qui, e NON
// in `DashboardPage.css` che dichiara gli stessi selettori: quel foglio viaggia
// col pezzo pigro della dashboard, e questo overlay e' montato anche sulla
// landing, dove quel pezzo non arriva mai. Stessa ragione, e stesso precedente,
// di RulesChannelsOverlay.
import '../dashboard/PlayModals.css';
// Stessa ragione per `.guest-challenge-notice` e `.guest-notice-login`: sono
// classi globali ma dichiarate nel foglio dei collegati, che solo quel
// componente importa.
import '../online/ConnectedUsersModal.css';
import './OverlaySoloIscritti.css';

/**
 * «Questa porta e' riservata agli iscritti», detto a SCHERMO.
 *
 * Il rifiuto di una porta riservata e' una risposta a un tocco, e per un tocco
 * dato in un menu che si chiude la risposta non ha dove stare: il riquadro che
 * i problemi del giorno alzavano dentro il foglio di «Gioca» era la quinta riga
 * di sei, e a 360x640 doveva portarsi in vista da se' per farsi leggere.
 *
 * Questo e' lo stesso rifiuto nella forma che la sfida diretta ha sempre avuto
 * (CustomTableOverlay): il foglio si chiude, e al suo posto sale una scheda —
 * card da 420px sul desktop, pagina intera sotto i 768px — con la testata che
 * nomina la porta, la ragione, e le due vie d'uscita.
 *
 * Non sa niente dei problemi del giorno: riceve il titolo e la CHIAVE i18n del
 * perche', perche' le porte riservate agli iscritti sono piu' di una e il
 * rifiuto deve avere una forma sola.
 */
interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Il nome della porta chiusa: lo legge la testata e lo rilegge `aria-label`. */
  titolo: string;
  /**
   * Il perche', come CHIAVE i18n e non come testo: chi monta questo overlay
   * decide quale porta sta rifiutando, non in che lingua lo dice.
   */
  messaggioKey: string;
  /** Il rientro nel proprio conto. */
  onSignIn?: () => void;
  /**
   * Il conto nuovo. Per un ospite chi lo riceve dirotta sulla CONVERSIONE: le
   * partite che ha giocato stanno sulla sua riga `users`, e una registrazione da
   * zero le lascerebbe appese a un'identita' che non riapre piu'.
   */
  onRegister?: () => void;
}

export default function OverlaySoloIscritti({
  isOpen,
  onClose,
  titolo,
  messaggioKey,
  onSignIn,
  onRegister,
}: Props) {
  const { t } = useTranslation();

  // Prima dell'early return: gli hook non stanno dietro a una condizione.
  // `onClose` passato al secondo gancio porta anche Escape, quindi qui non c'e'
  // nessun listener scritto a mano.
  useOverlayBackButton(isOpen, onClose);
  const riferimentoDialogo = useModaleAccessibile<HTMLDivElement>(isOpen, onClose);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal dashboard-modal play-online-modal overlay-solo-iscritti"
        ref={riferimentoDialogo}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        aria-label={titolo}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="play-online-head">
          <h2>{titolo}</h2>
          <button
            type="button"
            className="play-online-close"
            aria-label={t('common.close')}
            onClick={onClose}
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        <div className="play-online-body">
          {/* `role="alert"` sulla FRASE e non sul riquadro: una regione viva che
              contiene dei bottoni se li porta dietro a ogni ricalcolo, e quelli
              vanno raggiunti col Tab, non annunciati. */}
          <div className="guest-challenge-notice">
            <p role="alert">{t(messaggioKey)}</p>
            {/* Nessuna delle due maniglie chiude questo overlay, ed e' voluto:
                il modale d'accesso sale da se' al piano sopra (`--z-accesso`) ed
                e' lui che il gesto «indietro» chiude per primo. Chiudere un
                overlay mentre se ne apre un altro nello stesso commit spende due
                voci di cronologia e fa uscire dall'app al primo «indietro». */}
            {onSignIn && (
              <button type="button" className="guest-notice-login" onClick={onSignIn}>
                <KeyRound size={16} aria-hidden="true" />
                {t('auth.signIn')}
              </button>
            )}
            {onRegister && (
              <button
                type="button"
                className="guest-notice-login osi-registrati"
                onClick={onRegister}
              >
                <UserPlus size={16} aria-hidden="true" />
                {t('auth.signUp')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
